import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

import { COPY } from "../copy";

import { AdTypesModal } from "./overview/AdTypesModal";

const AD_TYPES_COPY = COPY.overview.adTypesModal;

const AdTypesModalContext = createContext<(() => void) | null>(null);

/**
 * Owns the one ad types dialog for the whole page, the same way
 * ForecastModalProvider owns the forecast one.
 *
 * It used to be local state on the Overview, which worked while the info button
 * on the ad type filter was the only trigger. The second trigger lives inside a
 * tooltip panel, and a panel unmounts the moment the pointer leaves it — a modal
 * rendered in there would be torn down on its way to being read. Hoisting it
 * also keeps the two triggers on a single dialog instance rather than one apiece.
 */
export function AdTypesModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);

  return (
    <AdTypesModalContext.Provider value={openModal}>
      {children}
      <AdTypesModal open={open} onClose={() => setOpen(false)} />
    </AdTypesModalContext.Provider>
  );
}

/**
 * Opens the ad types dialog. Returns null outside a provider so a component can
 * hide its trigger rather than render one that does nothing.
 */
export function useAdTypesModal(): (() => void) | null {
  return useContext(AdTypesModalContext);
}

/**
 * The explainer link as it appears inside a tooltip panel: white on the dark
 * panel, and on its own line so it reads as an action rather than as the last
 * clause of the definition above it.
 */
export function AdTypesTooltipLink() {
  const openModal = useAdTypesModal();
  if (!openModal) {
    return null;
  }

  return (
    <button
      type="button"
      className="text-fs-text-primary-inverse mt-2 block text-left text-[14px] leading-[20px] underline"
      onClick={openModal}
    >
      {AD_TYPES_COPY.link}
    </button>
  );
}

/**
 * Tooltip body for a figure that sums both ad types: its definition, then the
 * link to the dialog that defines the two.
 *
 * A helper rather than a fragment at the call site, so a second such figure
 * picks up the same affordance in the same shape instead of a hand-assembled
 * near-copy of it.
 *
 * Any tooltip built with this has to be rendered with `interactive` on
 * `HeaderTooltip`, or the link can't be clicked.
 */
export function adTypesTooltip(definition: string): ReactNode {
  return (
    <>
      {definition}
      <AdTypesTooltipLink />
    </>
  );
}
